import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'google_reviews_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Custom hook to fetch and cache Google Reviews data
 * Shares data across all components, avoiding multiple API calls
 */
export const useGoogleReviews = () => {
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        // Check localStorage cache first
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          
          // If cache is valid (less than 24h old), use it immediately
          if (age < CACHE_DURATION) {
            setReviewsData(data);
            setLoading(false);
            return;
          }
        }

        // Fetch fresh data from API
        const response = await axios.get(`${BACKEND_URL}/api/reviews`);
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          setReviewsData(data);
          
          // Save to localStorage cache
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError(err);
        
        // On error, try to use stale cache if available
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data } = JSON.parse(cached);
          setReviewsData(data);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  return {
    rating: reviewsData?.rating || 4.9,
    totalReviews: reviewsData?.total_reviews || 40,
    reviews: reviewsData?.reviews || [],
    loading,
    error
  };
};
